"use client";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import MarketingConsent from "@/components/forms/MarketingConsent";
import { appendMarketingConsent } from "@/lib/marketing-consent";
import Botcheck from "@/components/forms/Botcheck";
import { isBotSubmission } from "@/lib/botcheck";
import {
  DEFAULT_UNIT_QTY,
  SMALL_ORDER_NOTE,
  orderBandFor,
  withSmallPrefix,
} from "@/content/enterprise/order-minimum";
import { dgxSparkOems } from "@/content/enterprise/dgx-spark";
import { whatsappHref } from "@/lib/whatsapp";
import { submitQuotationForm } from "@/lib/web3forms-quotation";
import {
  ASUS_GX10_OFFER_ID,
  ASUS_GX10_MODEL,
  ASUS_GX10_TERMS,
  appendAsusGx10OfferFields,
  asusGx10OfferSelection,
  asusGx10Prefill,
  formatGx10Price,
} from "@/content/enterprise/asus-gx10-offer";

// Web3Forms: SAME access key as the calculator and the general enterprise form,
// DISTINCT format — request_type "DGX Spark Enquiry" and its own field set, so
// GB10 enquiries are filterable in the inbox and never blur with brand leads.
// Spec: docs/research/enterprise-brand-directory.md [C8].

const CAPACITIES = ["1 TB", "2 TB", "4 TB", "Advise me"];
const UNIT_PLANS = ["A single unit", "Two units linked (up to 405B params)", "A fleet for a team"];
const WORKLOADS = [
  "Prototyping and experimentation",
  "Fine-tuning models",
  "Local inference on our own data",
  "Edge, robotics or computer vision",
  "Not sure — advise me",
];
const TIMELINES = ["This month", "This quarter", "Budgeting ahead"];

function DgxEnquiryForm() {
  const locale = useLocale();
  const params = useSearchParams();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };
  const lp = (href: string) => getLocalizedPathname(href, locale);

  // ?model=ThinkStation PGX arrives from the comparison grid on the product page.
  const preselect = params.get("model");
  const initialModel =
    dgxSparkOems.find((o) => o.model === preselect)?.model ?? "";
  const offerPrefill = asusGx10Prefill(params);

  const [model, setModel] = useState(initialModel);
  const [qty, setQty] = useState(offerPrefill?.quantity ?? DEFAULT_UNIT_QTY);
  const [capacity, setCapacity] = useState<string>(offerPrefill?.storage ?? "Advise me");
  const [plan, setPlan] = useState("");
  const [workload, setWorkload] = useState("");
  const [timeline, setTimeline] = useState("");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const validQty = /^\d+$/.test(qty) && Number(qty) >= 1;
  const ready = Boolean(model) && Boolean(email) && Boolean(company) && validQty;
  const orderBand = orderBandFor([{ measure: "units", qty }]);
  const gx10Campaign = model === ASUS_GX10_MODEL && params.get("offer") === ASUS_GX10_OFFER_ID;
  const offer = asusGx10OfferSelection(model, capacity, params.get("offer"));

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ready) return;
    // Honeypot ticked: show success, send nothing.
    if (isBotSubmission(e.currentTarget)) {
      setStatus("sent");
      return;
    }
    setStatus("sending");
    const key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    if (!key) {
      setStatus("error");
      return;
    }
    const body = new FormData();
    body.append("access_key", key ?? "");
    body.append("request_type", "DGX Spark Enquiry");
    body.append("source", "dgx-spark-enquiry");
    body.append("subject", withSmallPrefix(`DGX Spark enquiry — ${model} ${capacity}${gx10Campaign ? " / 128 GB" : ""} × ${qty} — ${company}`, orderBand));
    body.append("from_name", "Anchor Sprint Website");
    body.append("name", name);
    body.append("company", company);
    body.append("email", email);
    body.append("phone", phone);
    body.append("product", "NVIDIA DGX Spark (GB10)");
    body.append("model", model);
    body.append("quantity", qty);
    body.append("storage", capacity);
    body.append("deployment", plan);
    body.append("workload", workload);
    body.append("timeline", timeline);
    body.append("order_band", orderBand);
    body.append("notes", notes);
    appendAsusGx10OfferFields(body, model, capacity, params.get("offer"));
    appendMarketingConsent(body, marketingConsent, locale);
    try {
      await submitQuotationForm(body);
      setStatus("sent");
      if (typeof window !== "undefined" && "gtag" in window) {
        (window as unknown as { gtag: (...a: unknown[]) => void }).gtag("event", "dgx_spark_enquiry_submit", {
          model,
          quantity: qty,
          brands: "NVIDIA DGX Spark",
          order_band: orderBand,
          storage: capacity,
          ...(gx10Campaign ? { offer_id: ASUS_GX10_OFFER_ID } : {}),
        });
      }
    } catch {
      setStatus("error");
    }
  };

  const label = "block text-xs font-semibold uppercase tracking-[0.14em] text-neutral-muted mb-2";
  const input =
    "w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-neutral-emphasis placeholder:text-neutral-muted/60 focus:border-primary focus:outline-none";
  const pill = (active: boolean) =>
    `inline-flex min-h-11 items-center rounded-full border px-4 py-1.5 text-sm transition ${
      active
        ? "border-primary bg-primary/10 font-medium text-primary"
        : "border-border bg-card text-neutral-muted hover:border-primary/40 hover:text-primary"
    }`;

  if (status === "sent") {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-10 text-center">
        <Check className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h2 className="mt-4 font-display text-2xl font-bold text-neutral-emphasis">
          {t(locale, "Enquiry received.")}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-neutral-muted">
          {t(
            locale,
            "We will come back with a quotation in RM, usually within two working days — and we will say so if a Spark is not the right machine for the workload you described.",
          )}
        </p>
        <a
          href={whatsappHref("Hi Anchor Sprint, I just sent a DGX Spark enquiry and have a follow-up question (from your DGX Spark enquiry page).")}
          target="_blank"
          rel="noopener noreferrer"
          data-track-event="contact_whatsapp_click"
          data-track-location="dgx_spark_enquiry_success"
          data-track-phone="601159246128"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary px-6 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t(locale, "WhatsApp us")}
        </a>
      </div>
    );
  }

  return (
    <motion.form {...inView} onSubmit={submit} className="space-y-8">
      {gx10Campaign && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-5" role="status">
          <h2 className="font-display text-lg font-semibold text-neutral-emphasis">
            ASUS Ascent GX10 · 128 GB · {t(locale, capacity)}
          </h2>
          {offer && (
            <p className="mt-2 text-sm font-semibold text-primary">
              {t(locale, "Advertised price per unit")}: {formatGx10Price(offer.priceRm)}
            </p>
          )}
          <p className="mt-2 text-xs leading-relaxed text-neutral-muted">{t(locale, ASUS_GX10_TERMS)}</p>
        </div>
      )}
      {/* Model — the whole reason this form exists separately */}
      <div>
        <span className={label}>{t(locale, "Which build?")}</span>
        <ul className="grid gap-2 sm:grid-cols-2">
          {dgxSparkOems.map((o) => {
            const active = model === o.model;
            return (
              <li key={o.model}>
                <button
                  type="button"
                  onClick={() => setModel(o.model)}
                  aria-pressed={active}
                  className={`w-full rounded-lg border p-3 text-left transition ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                    {o.brand}
                  </span>
                  <span className="mt-0.5 block text-sm font-semibold text-neutral-emphasis">
                    {o.model}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {!model && (
          <p className="mt-2 text-sm text-neutral-muted">
            {t(locale, "Pick a build — or choose any one and tell us in the notes that you want them compared.")}
          </p>
        )}
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="dgx-qty" className={label}>{t(locale, "How many units?")}</label>
          <input
            id="dgx-qty"
            type="number"
            min={1}
            step={1}
            required
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className={input}
          />
          {orderBand === "small" && (
            <p
              role="status"
              className="mt-2 rounded-lg border border-info/30 bg-info/5 p-3 text-sm text-neutral-emphasis"
            >
              {t(locale, model === ASUS_GX10_MODEL && offerPrefill
                ? "Your request is for fewer than 10 units. Our team will confirm availability and the quotation."
                : SMALL_ORDER_NOTE)}
            </p>
          )}
        </div>
        <div>
          <span className={label}>{t(locale, "Storage")}</span>
          <div className="flex flex-wrap gap-2">
            {CAPACITIES.map((c) => (
              <button key={c} type="button" aria-pressed={capacity === c} onClick={() => setCapacity(c)} className={pill(capacity === c)}>
                {t(locale, c)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <span className={label}>{t(locale, "Deployment")}</span>
        <div className="flex flex-wrap gap-2">
          {UNIT_PLANS.map((p) => (
            <button key={p} type="button" onClick={() => setPlan(p)} className={pill(plan === p)}>
              {t(locale, p)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className={label}>{t(locale, "What will it run?")}</span>
        <div className="flex flex-wrap gap-2">
          {WORKLOADS.map((w) => (
            <button key={w} type="button" onClick={() => setWorkload(w)} className={pill(workload === w)}>
              {t(locale, w)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className={label}>{t(locale, "Timeline")}</span>
        <div className="flex flex-wrap gap-2">
          {TIMELINES.map((tl) => (
            <button key={tl} type="button" onClick={() => setTimeline(tl)} className={pill(timeline === tl)}>
              {t(locale, tl)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="dgx-name" className={label}>{t(locale, "Your name")}</label>
          <input id="dgx-name" value={name} onChange={(e) => setName(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="dgx-company" className={label}>{t(locale, "Company")}</label>
          <input id="dgx-company" required value={company} onChange={(e) => setCompany(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="dgx-email" className={label}>{t(locale, "Work email")}</label>
          <input id="dgx-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="dgx-phone" className={label}>{t(locale, "Phone (WhatsApp optional)")}</label>
          <input id="dgx-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={input} />
        </div>
      </div>

      <div>
        <label htmlFor="dgx-notes" className={label}>
          {t(locale, "Anything else — model sizes you need to run, data residency, existing hardware")}
        </label>
        <textarea id="dgx-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
      </div>

      <Botcheck />
      <MarketingConsent
        id="dgx-marketing-consent"
        checked={marketingConsent}
        onChange={setMarketingConsent}
        locale={locale}
      />

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "sending" || !ready}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "sending" ? t(locale, "Sending…") : t(locale, "Request quotation")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
        <Link
          href={lp("/enterprise/dgx-spark-malaysia/")}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t(locale, "Compare the builds first")}
        </Link>
      </div>
      {status === "error" && (
        <p className="text-sm text-destructive">
          {t(locale, "Something went wrong sending the form — please try again, or WhatsApp us instead.")}
        </p>
      )}
    </motion.form>
  );
}

export default function DgxSparkEnquiry() {
  const locale = useLocale();
  return (
    <main className="bg-background">
      <section className="pb-16 pt-24 lg:pt-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "DGX Spark Enquiry")}
          </p>
          <h1 className="mt-5 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {t(locale, "Get a DGX Spark quotation in Ringgit")}
          </h1>
          <p className="mt-5 text-lg text-neutral-muted">
            {t(
              locale,
              "Every OEM build is quotable — NVIDIA, ASUS, Dell, HP, Lenovo, Acer, Gigabyte and MSI. Tell us the build and the workload; we come back in RM with SST stated, usually within two working days.",
            )}
          </p>
          <div className="mt-12">
            {/* Static export: useSearchParams must sit inside Suspense. */}
            <Suspense fallback={null}>
              <DgxEnquiryForm />
            </Suspense>
          </div>
        </div>
      </section>
    </main>
  );
}
